import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts'
import { adminApi, planosApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select, Toggle } from '../../components/ui/Input'
import { Card, CardHeader, CardBody, KpiCard, AvatarIcon } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState, ErrorState, EmptyState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { Tabs } from '../../components/ui/Navigation'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import {
  StatusAssinatura, STATUS_ASSINATURA_LABEL, StatusPagamento, STATUS_PAGAMENTO_LABEL,
  CicloCobranca, CICLO_LABEL, GATEWAY_LABEL,
} from '../../lib/enums'
import { formatMoney, formatDate, formatDateTime, cn } from '../../lib/utils'
import type { Assinante, PagamentoAdmin, OrigemCancelamento } from '../../types'

type Aba = 'painel' | 'assinantes' | 'pagamentos' | 'canceladas'

const ABAS = [
  { key: 'painel' as const, label: 'Painel', icon: 'ri-dashboard-line' },
  { key: 'assinantes' as const, label: 'Assinantes', icon: 'ri-team-line' },
  { key: 'pagamentos' as const, label: 'Pagamentos', icon: 'ri-money-dollar-circle-line' },
  { key: 'canceladas' as const, label: 'Cobranças canceladas', icon: 'ri-close-circle-line' },
]

const CORES = ['#8B949E', '#FFB300', '#22C55E']
const TOOLTIP = { background: '#1B2330', border: '1px solid #2D3748', borderRadius: 4, fontSize: 12 }
const EIXO = { fontSize: 11, fill: '#8B949E' }

const STATUS_ASSINATURA_VARIANT: Record<number, 'income' | 'warning' | 'expense' | 'muted'> = {
  [StatusAssinatura.Ativa]: 'income',
  [StatusAssinatura.Trial]: 'warning',
  [StatusAssinatura.Pendente]: 'warning',
  [StatusAssinatura.Inadimplente]: 'expense',
  [StatusAssinatura.Cancelada]: 'muted',
}

export function AdminPage() {
  const [aba, setAba] = useState<Aba>('painel')

  return (
    <>
      <PageHeader title="Administração" />
      <Card>
        <CardHeader title="Gestão de assinaturas" icon="ri-shield-star-line" />
        <Tabs tabs={ABAS} active={aba} onChange={setAba} className="px-4" />
        <CardBody>
          {aba === 'painel' && <Painel />}
          {aba === 'assinantes' && <Assinantes />}
          {aba === 'pagamentos' && <Pagamentos />}
          {aba === 'canceladas' && <CobrancasCanceladas />}
        </CardBody>
      </Card>
    </>
  )
}

function Painel() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['admin-painel'], queryFn: () => adminApi.painel(6) })

  if (isLoading) return <LoadingState />
  if (isError || !data) return <ErrorState message="Erro ao carregar o painel." />

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard title="Receita recorrente" value={formatMoney(data.receitaRecorrenteMensal)} icon="ri-repeat-line" tone="primary" />
        <KpiCard title="Recebido no mês" value={formatMoney(data.receitaRecebidaNoMes)} icon="ri-money-dollar-circle-line" tone="income" />
        <KpiCard
          title="Em aberto"
          value={formatMoney(data.valorPendente)}
          icon="ri-time-line"
          tone="warning"
          change={data.valorVencido > 0 ? { value: `${formatMoney(data.valorVencido)} vencido`, positive: false } : undefined}
        />
        <KpiCard
          title="Churn do mês"
          value={`${data.churnPercentual.toFixed(1)}%`}
          icon="ri-user-unfollow-line"
          tone={data.churnPercentual > 5 ? 'expense' : 'info'}
          change={{ value: `${data.canceladosNoMes} cancelamentos`, positive: data.canceladosNoMes === 0 }}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { rotulo: 'Usuários', valor: data.totalUsuarios, icone: 'ri-group-line' },
          { rotulo: 'Assinantes pagantes', valor: data.assinantesAtivos, icone: 'ri-vip-crown-line' },
          { rotulo: 'Em teste', valor: data.emTeste, icone: 'ri-timer-line' },
          { rotulo: 'Inadimplentes', valor: data.inadimplentes, icone: 'ri-error-warning-line' },
        ].map((k) => (
          <div key={k.rotulo} className="flex items-center gap-3 rounded border border-border/60 p-3">
            <AvatarIcon icon={k.icone} tone="muted" size="sm" />
            <div>
              <p className="text-lg font-semibold text-white">{k.valor}</p>
              <p className="text-2xs text-muted">{k.rotulo}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="rounded border border-border/60 p-4 xl:col-span-2">
          <h5 className="mb-3 text-15 font-medium text-white">Receita, entradas e saídas por mês</h5>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.serie} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
              <XAxis dataKey="label" tick={EIXO} axisLine={false} tickLine={false} />
              <YAxis yAxisId="dinheiro" tick={EIXO} axisLine={false} tickLine={false} tickFormatter={(v) => `${(Number(v) / 1000).toFixed(0)}k`} />
              <YAxis yAxisId="pessoas" orientation="right" tick={EIXO} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                formatter={(valor, nome) => nome === 'Receita' ? formatMoney(Number(valor)) : valor}
                contentStyle={TOOLTIP}
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: '#8B949E' }} iconType="circle" />
              <Bar yAxisId="dinheiro" dataKey="receita" name="Receita" fill="#FFB300" radius={[3, 3, 0, 0]} maxBarSize={26} />
              <Bar yAxisId="pessoas" dataKey="novos" name="Novos" fill="#22C55E" radius={[3, 3, 0, 0]} maxBarSize={18} />
              <Bar yAxisId="pessoas" dataKey="cancelados" name="Cancelados" fill="#EF4444" radius={[3, 3, 0, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded border border-border/60 p-4">
          <h5 className="mb-3 text-15 font-medium text-white">Assinantes por plano</h5>
          {data.porPlano.every((p) => p.assinantes === 0) ? (
            <p className="py-10 text-center text-13 text-muted">Ainda não há assinantes.</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data.porPlano} dataKey="assinantes" nameKey="nome" innerRadius={50} outerRadius={78} strokeWidth={0} paddingAngle={2}>
                    {data.porPlano.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP} />
                </PieChart>
              </ResponsiveContainer>
              <ul className="mt-3 space-y-2">
                {data.porPlano.map((p, i) => (
                  <li key={p.codigo} className="flex items-center justify-between text-13">
                    <span className="flex items-center gap-2 text-muted">
                      <span className="h-2 w-2 rounded-full" style={{ background: CORES[i % CORES.length] }} /> {p.nome}
                    </span>
                    <span className="text-white">{p.assinantes} · {formatMoney(p.receitaMensal)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 border-t border-border/60 pt-3 text-13 text-muted">
                Ticket médio: <strong className="text-white">{formatMoney(data.ticketMedio)}</strong>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Assinantes() {
  const qc = useQueryClient()
  const toast = useToast()
  const [busca, setBusca] = useState('')
  const [status, setStatus] = useState<StatusAssinatura | ''>('')
  const [alterando, setAlterando] = useState<Assinante | null>(null)
  const [form, setForm] = useState<{ codigoPlano: string; ciclo: CicloCobranca; comoCortesia: boolean }>(
    { codigoPlano: 'PREMIUM', ciclo: CicloCobranca.Mensal, comoCortesia: false })
  const [cancelando, setCancelando] = useState<Assinante | null>(null)

  const { data: assinantes = [], isLoading } = useQuery({
    queryKey: ['admin-assinantes', status, busca],
    queryFn: () => adminApi.assinantes({ status: status || undefined, busca: busca || undefined }),
  })
  const { data: planos = [] } = useQuery({ queryKey: ['planos'], queryFn: planosApi.list })

  const alterarPlano = useMutation({
    mutationFn: () => adminApi.alterarPlano(alterando!.userId, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-assinantes'] })
      qc.invalidateQueries({ queryKey: ['admin-painel'] })
      toast.success('Plano alterado.')
      setAlterando(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const cancelar = useMutation({
    mutationFn: () => adminApi.cancelarAssinatura(cancelando!.userId, 'Cancelado pelo administrador'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-assinantes'] })
      qc.invalidateQueries({ queryKey: ['admin-painel'] })
      toast.success('Assinatura cancelada.')
      setCancelando(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64"><Input icon="ri-search-line" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome ou e-mail" aria-label="Buscar assinante" /></div>
        <div className="w-48">
          <Select value={status} onChange={(e) => setStatus(e.target.value ? Number(e.target.value) as StatusAssinatura : '')} aria-label="Situação">
            <option value="">Todas as situações</option>
            {Object.entries(STATUS_ASSINATURA_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </div>
        <span className="text-13 text-muted">{assinantes.length} usuários</span>
      </div>

      {isLoading ? <LoadingState /> : assinantes.length === 0 ? (
        <EmptyState icon="ri-team-line" message="Nenhum usuário encontrado." />
      ) : (
        <Table>
          <THead>
            <tr><Th>Usuário</Th><Th>Plano</Th><Th>Situação</Th><Th>Valor</Th><Th>Renova</Th><Th>Desde</Th><Th className="text-right">Ações</Th></tr>
          </THead>
          <tbody>
            {assinantes.map((a) => (
              <Tr key={a.userId}>
                <Td>
                  <p className="font-medium text-white">{a.nome}</p>
                  <p className="text-2xs text-muted">{a.email}</p>
                </Td>
                <Td className="text-white">{a.plano}</Td>
                <Td>
                  <Badge variant={STATUS_ASSINATURA_VARIANT[a.status] ?? 'muted'}>{STATUS_ASSINATURA_LABEL[a.status]}</Badge>
                  {a.motivoCancelamento && <p className="mt-1 max-w-[220px] truncate text-2xs text-muted" title={a.motivoCancelamento}>{a.motivoCancelamento}</p>}
                </Td>
                <Td className="whitespace-nowrap text-muted">{a.valor > 0 ? `${formatMoney(a.valor)} · ${CICLO_LABEL[a.ciclo]}` : '—'}</Td>
                <Td className="whitespace-nowrap text-muted">{a.renovaEm ? formatDate(a.renovaEm) : a.trialFimEm ? `teste até ${formatDate(a.trialFimEm)}` : '—'}</Td>
                <Td className="whitespace-nowrap text-muted">{formatDate(a.criadoEm)}</Td>
                <Td className="whitespace-nowrap text-right">
                  <IconButton icon="ri-vip-crown-line" label={`Alterar plano de ${a.nome}`} tone="primary" onClick={() => { setAlterando(a); setForm({ codigoPlano: 'PREMIUM', ciclo: CicloCobranca.Mensal, comoCortesia: false }) }} />
                  <IconButton icon="ri-close-circle-line" label={`Cancelar assinatura de ${a.nome}`} tone="danger" disabled={a.valor === 0} onClick={() => setCancelando(a)} />
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal
        open={!!alterando}
        onClose={() => setAlterando(null)}
        title={`Plano de ${alterando?.nome ?? ''}`}
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setAlterando(null)}>Cancelar</Button>
            <Button icon="ri-save-line" loading={alterarPlano.isPending} onClick={() => alterarPlano.mutate()}>Aplicar</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select label="Plano" value={form.codigoPlano} onChange={(e) => setForm((f) => ({ ...f, codigoPlano: e.target.value }))}>
            {planos.map((p) => <option key={p.codigo} value={p.codigo}>{p.nome}</option>)}
          </Select>
          <Select label="Ciclo" value={form.ciclo} onChange={(e) => setForm((f) => ({ ...f, ciclo: Number(e.target.value) as CicloCobranca }))}>
            {Object.entries(CICLO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
          <div className="rounded bg-surface2 p-3">
            <Toggle label="Liberar como cortesia, sem cobrar" checked={form.comoCortesia} onChange={(v) => setForm((f) => ({ ...f, comoCortesia: v }))} />
          </div>
          <p className="text-2xs text-muted">Sem cortesia, uma cobrança é gerada e o plano libera quando o pagamento for confirmado.</p>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!cancelando}
        onClose={() => setCancelando(null)}
        onConfirm={() => cancelar.mutate()}
        loading={cancelar.isPending}
        title="Cancelar assinatura?"
        confirmLabel="Sim, cancelar"
        message={<>A assinatura de <strong className="text-white">{cancelando?.nome}</strong> será cancelada e a conta volta para o plano grátis.</>}
      />
    </div>
  )
}

function Pagamentos() {
  const qc = useQueryClient()
  const toast = useToast()
  const [status, setStatus] = useState<StatusPagamento | ''>(StatusPagamento.Pendente)
  const [baixando, setBaixando] = useState<PagamentoAdmin | null>(null)
  const [metodo, setMetodo] = useState('Pix')
  const [cancelando, setCancelando] = useState<PagamentoAdmin | null>(null)
  const [motivoCancelamento, setMotivoCancelamento] = useState('')

  const { data: pagamentos = [], isLoading } = useQuery({
    queryKey: ['admin-pagamentos', status],
    queryFn: () => adminApi.pagamentos(status || undefined),
  })

  const baixar = useMutation({
    mutationFn: () => adminApi.baixarPagamento(baixando!.id, { metodoPagamento: metodo }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-pagamentos'] })
      qc.invalidateQueries({ queryKey: ['admin-painel'] })
      toast.success('Pagamento baixado e assinatura ativada.')
      setBaixando(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const cancelarCobranca = useMutation({
    mutationFn: () => adminApi.cancelarCobranca(cancelando!.id, motivoCancelamento.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-pagamentos'] })
      qc.invalidateQueries({ queryKey: ['admin-cobrancas-canceladas'] })
      qc.invalidateQueries({ queryKey: ['admin-painel'] })
      toast.success('Cobrança cancelada.')
      setCancelando(null)
      setMotivoCancelamento('')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const total = pagamentos.reduce((s, p) => s + p.valor, 0)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Select value={status} onChange={(e) => setStatus(e.target.value ? Number(e.target.value) as StatusPagamento : '')} aria-label="Situação do pagamento">
            <option value="">Todas as situações</option>
            {Object.entries(STATUS_PAGAMENTO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </div>
        <span className="text-13 text-muted">{pagamentos.length} cobranças · {formatMoney(total)}</span>
      </div>

      {isLoading ? <LoadingState /> : pagamentos.length === 0 ? (
        <EmptyState icon="ri-money-dollar-circle-line" message="Nenhuma cobrança nessa situação." />
      ) : (
        <Table>
          <THead>
            <tr><Th>Usuário</Th><Th>Plano</Th><Th>Vencimento</Th><Th>Valor</Th><Th>Situação</Th><Th>Meio</Th><Th className="text-right">Ações</Th></tr>
          </THead>
          <tbody>
            {pagamentos.map((p) => (
              <Tr key={p.id}>
                <Td>
                  <p className="font-medium text-white">{p.usuario}</p>
                  <p className="text-2xs text-muted">{p.email}</p>
                </Td>
                <Td className="text-muted">
                  {p.plano}
                  {p.planoDestino && <p className="text-2xs">Troca para {p.planoDestino.nome}</p>}
                </Td>
                <Td className={cn('whitespace-nowrap', p.vencido ? 'text-expense' : 'text-muted')}>
                  {formatDate(p.vencimento)}{p.vencido && ' · vencido'}
                </Td>
                <Td className="whitespace-nowrap font-medium text-white">{formatMoney(p.valor)}</Td>
                <Td>
                  <Badge variant={p.status === StatusPagamento.Pago ? 'income' : p.status === StatusPagamento.Pendente ? 'warning' : 'muted'}>
                    {STATUS_PAGAMENTO_LABEL[p.status]}
                  </Badge>
                  {p.canceladoEm && (
                    <p className="mt-1 max-w-[240px] text-2xs text-muted">
                      Cancelada em {formatDateTime(p.canceladoEm)}
                      {p.canceladoPorNome ? ` por ${p.canceladoPorNome}` : p.canceladoOrigem ? ` (${ORIGEM_LABEL[p.canceladoOrigem] ?? p.canceladoOrigem})` : ''}
                      {p.canceladoMotivo ? `: ${p.canceladoMotivo}` : ''}
                    </p>
                  )}
                </Td>
                <Td className="text-muted">{p.gateway ? GATEWAY_LABEL[p.gateway] : p.metodoPagamento ?? '—'}</Td>
                <Td className="whitespace-nowrap text-right">
                  {p.status === StatusPagamento.Pendente && (
                    <IconButton icon="ri-check-double-line" label={`Dar baixa em ${p.usuario}`} tone="success" onClick={() => setBaixando(p)} />
                  )}
                  {(p.status === StatusPagamento.Pendente || p.status === StatusPagamento.Falhou) && !p.canceladoEm && (
                    <IconButton icon="ri-close-circle-line" label={`Cancelar cobrança de ${p.usuario}`} tone="danger"
                      onClick={() => { setCancelando(p); setMotivoCancelamento('') }} />
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal
        open={!!baixando}
        onClose={() => setBaixando(null)}
        title="Dar baixa no pagamento"
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setBaixando(null)}>Cancelar</Button>
            <Button icon="ri-check-double-line" loading={baixar.isPending} onClick={() => baixar.mutate()}>Confirmar baixa</Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-13 text-muted">
            {formatMoney(baixando?.valor ?? 0)} de <strong className="text-white">{baixando?.usuario}</strong>. A assinatura é ativada e a renovação é empurrada.
          </p>
          <Select label="Meio de pagamento" value={metodo} onChange={(e) => setMetodo(e.target.value)}>
            {['Pix', 'Transferência', 'Cartão', 'Boleto', 'Dinheiro', 'Manual'].map((m) => <option key={m} value={m}>{m}</option>)}
          </Select>
        </div>
      </Modal>

      <Modal
        open={!!cancelando}
        onClose={() => setCancelando(null)}
        title="Cancelar cobrança"
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setCancelando(null)}>Voltar</Button>
            <Button variant="danger" icon="ri-close-circle-line" loading={cancelarCobranca.isPending}
              disabled={!motivoCancelamento.trim()} onClick={() => cancelarCobranca.mutate()}>
              Cancelar cobrança
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-13 text-muted">
            A cobrança de {formatMoney(cancelando?.valor ?? 0)} de <strong className="text-white">{cancelando?.usuario}</strong> será cancelada.
            Se for de uma troca de plano, a troca também cai. Isso não pode ser desfeito.
          </p>
          <Input label="Motivo (obrigatório)" value={motivoCancelamento} maxLength={500}
            onChange={(e) => setMotivoCancelamento(e.target.value)} placeholder="Por que está cancelando?" />
        </div>
      </Modal>
    </div>
  )
}

const ORIGEM_LABEL: Record<string, string> = { Admin: 'Admin', Sistema: 'Sistema', Usuario: 'Usuário' }

function CobrancasCanceladas() {
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const [origem, setOrigem] = useState<OrigemCancelamento | ''>('')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-cobrancas-canceladas', de, ate, origem],
    queryFn: () => adminApi.cobrancasCanceladas({ de: de || undefined, ate: ate || undefined, origem: origem || undefined }),
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44"><Input label="De" type="date" value={de} max={ate || undefined} onChange={(e) => setDe(e.target.value)} /></div>
        <div className="w-44"><Input label="Até" type="date" value={ate} min={de || undefined} onChange={(e) => setAte(e.target.value)} /></div>
        <div className="w-44">
          <Select label="Origem" value={origem} onChange={(e) => setOrigem(e.target.value as OrigemCancelamento | '')}>
            <option value="">Todas</option>
            <option value="Admin">Admin</option>
            <option value="Sistema">Sistema</option>
            <option value="Usuario">Usuário</option>
          </Select>
        </div>
      </div>

      {isLoading ? <LoadingState /> : isError || !data ? (
        <ErrorState message="Erro ao carregar o relatório." />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <KpiCard title="Cobranças canceladas" value={String(data.totais.quantidade)} icon="ri-close-circle-line" tone="expense" />
            <KpiCard title="Valor total" value={formatMoney(data.totais.valorTotal)} icon="ri-money-dollar-circle-line" tone="warning" />
          </div>

          {data.itens.length === 0 ? (
            <EmptyState icon="ri-close-circle-line" message="Nenhuma cobrança cancelada nesse período." />
          ) : (
            <Table>
              <THead>
                <tr><Th>Usuário</Th><Th>Plano</Th><Th>Valor</Th><Th>Vencimento</Th><Th>Cancelada em</Th><Th>Por</Th><Th>Origem</Th><Th>Motivo</Th></tr>
              </THead>
              <tbody>
                {data.itens.map((i) => (
                  <Tr key={i.pagamentoId}>
                    <Td>
                      <p className="font-medium text-white">{i.usuarioNome}</p>
                      <p className="text-2xs text-muted">{i.usuarioEmail}</p>
                    </Td>
                    <Td className="text-muted">{i.planoNome}</Td>
                    <Td className="whitespace-nowrap font-medium text-white">{formatMoney(i.valor)}</Td>
                    <Td className="whitespace-nowrap text-muted">{formatDate(i.vencimento)}</Td>
                    <Td className="whitespace-nowrap text-muted">{formatDateTime(i.canceladoEm)}</Td>
                    <Td className="text-muted">{i.canceladoPorNome ?? '—'}</Td>
                    <Td><Badge variant="muted">{ORIGEM_LABEL[i.origem] ?? i.origem}</Badge></Td>
                    <Td className="max-w-[260px] text-muted">{i.motivo ?? '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </div>
  )
}

